// Kourse fee configuration
export const KOURSE_FEE_PERCENT = 10; // 10% TTC (includes all fees) - default rate

export interface PricingBreakdown {
  basePrice: number; // Price set by organizer (cents)
  totalFees: number; // Total fees (cents)
  runnerPays: number; // What the runner actually pays (cents)
  organizerReceives: number; // What the organizer receives (cents)
}

// Community pricing with dynamic fees based on price thresholds
export interface CommunityPricingBreakdown {
  basePrice: number;        // Prix choisi par l'orga (en cents)
  feeCents: number;         // Frais Kourse (en cents)
  feePercent: number;       // Pourcentage appliqué (pour calcul interne)
  organizerReceives: number; // Ce que l'orga reçoit (en cents)
  runnerPays: number;       // Ce que le participant paie = basePrice (en cents)
}

/**
 * Dynamic fee percent based on price thresholds.
 * Ensures Kourse profitability on all transactions (covers 0.25€ + 1.8% Mollie fees).
 *
 * Fee structure:
 * - < 2€: 50%
 * - 2€ - 2.99€: 30%
 * - 3€ - 3.99€: 25%
 * - 4€ - 4.99€: 20%
 * - ≥ 5€: 10% (standard)
 */
function getDynamicFeePercent(priceCents: number): number {
  const priceEuros = priceCents / 100;
  if (priceEuros < 2) return 50;
  if (priceEuros < 3) return 30;
  if (priceEuros < 4) return 25;
  if (priceEuros < 5) return 20;
  return 10;
}

/**
 * Calculate fees for community events with dynamic pricing
 */
export function calculateCommunityPricing(priceCents: number): CommunityPricingBreakdown {
  if (priceCents === 0) {
    return { basePrice: 0, feeCents: 0, feePercent: 0, organizerReceives: 0, runnerPays: 0 };
  }

  const feePercent = getDynamicFeePercent(priceCents);
  const feeCents = Math.max(50, Math.round(priceCents * feePercent / 100));
  const organizerReceives = priceCents - feeCents;

  return {
    basePrice: priceCents,
    feeCents,
    feePercent,
    organizerReceives,
    runnerPays: priceCents,
  };
}

/**
 * Calculate fees based on a price in cents
 * @param amountCents - The amount in cents. Interpretation depends on `feesIncluded`:
 *   - feesIncluded = true  : amountCents is the BASE price set by the organizer.
 *                            Commission = amountCents * rate%.
 *   - feesIncluded = false : amountCents is the TOTAL paid by the runner (base + fees).
 *                            Commission = amountCents - basePrice, where
 *                            basePrice = amountCents / (1 + rate/100).
 * @param customRate - Optional custom commission rate (e.g., 8.5 for 8.5%). If null/undefined, uses dynamic rate
 * @param feesIncluded - Whether the fees are included in `amountCents` (defaults to true for
 *   backward compatibility with callers that pass the base price).
 */
export function calculateFees(
  amountCents: number,
  customRate?: number | null,
  feesIncluded: boolean = true,
): { totalFees: number; feePercent: number } {
  if (amountCents === 0) {
    return { totalFees: 0, feePercent: 0 };
  }

  const rate = customRate ?? getDynamicFeePercent(amountCents);

  if (!feesIncluded) {
    // Fees were added on top: amountCents = basePrice + fees, so derive base back out.
    const basePrice = Math.round(amountCents / (1 + rate / 100));
    const totalFees = amountCents - basePrice;
    return { totalFees, feePercent: rate };
  }

  if (customRate != null) {
    // Negotiated rate: no minimum fee
    const totalFees = Math.round(amountCents * (rate / 100));
    return { totalFees, feePercent: rate };
  }

  const totalFees = Math.max(50, Math.round(amountCents * rate / 100));
  return { totalFees, feePercent: rate };
}

/**
 * Calculate full pricing breakdown
 * @param priceCents - The base price set by the organizer (in cents)
 * @param feesIncluded - If true, fees are deducted from organizer's revenue. If false, fees are added to runner's price.
 * @param customRate - Optional custom commission rate (e.g., 8.5 for 8.5%). If null/undefined, uses default 10%
 */
export function calculatePricing(priceCents: number, feesIncluded: boolean, customRate?: number | null): PricingBreakdown {
  if (priceCents === 0) {
    return {
      basePrice: 0,
      totalFees: 0,
      runnerPays: 0,
      organizerReceives: 0,
    };
  }

  const { totalFees } = calculateFees(priceCents, customRate);

  if (feesIncluded) {
    // Fees included in price - organizer absorbs the fees
    // Runner pays the displayed price, organizer receives price minus fees
    return {
      basePrice: priceCents,
      totalFees,
      runnerPays: priceCents,
      organizerReceives: priceCents - totalFees,
    };
  } else {
    // Fees added to price - runner pays extra
    // Runner pays base price + fees, organizer receives the full base price
    return {
      basePrice: priceCents,
      totalFees,
      runnerPays: priceCents + totalFees,
      organizerReceives: priceCents,
    };
  }
}

/**
 * Format price in cents to currency string
 */
export function formatPrice(cents: number, currency: string = 'EUR'): string {
  if (cents === 0) return 'Gratuit';
  return (cents / 100).toLocaleString('fr-FR', {
    style: 'currency',
    currency,
  });
}

/**
 * Calculate estimated revenue if all races are full
 */
export function calculateEstimatedRevenue(
  races: Array<{ price_cents: number; fees_included: boolean; max_participants: number | null }>
): number {
  return races.reduce((total, race) => {
    const participants = race.max_participants || 0;
    const pricing = calculatePricing(race.price_cents, race.fees_included);
    return total + (pricing.organizerReceives * participants);
  }, 0);
}
